import GroupPicker from '@app/features/Group/GroupPicker';
import {
  useListGroupsQuery,
  useGetUsersQuery,
  useUpdateUserGroupsMutation,
} from '@app/types/blackroom-api';
import { useCallback, useEffect, useState } from 'react';
import { UserGroupPickerProps } from './types';
import { RowGroup } from '@app/types/blackroom-app';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { Center } from '@mantine/core';
import LoaderIcon from '@app/shared/ui-kit/atoms/LoaderIcon';

export default function UserGroupPicker({
  user,
  workspace,
  ...props
}: UserGroupPickerProps) {
  /* ********************************** LOCAL ******************************** */
  const { t } = useTranslation(['Common']);
  const [groups, setGroups] = useState<RowGroup[]>([]);
  const [userGroups, setUserGroups] = useState<RowGroup[]>([]);

  // get the groups of the given user
  const { data: usersData, loading: usersLoading } = useGetUsersQuery({
    variables: {
      filter: { ids: [user.id], workspaceId: workspace.id },
      workspaceId: workspace.id,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // get all workspace groups
  const { data: groupsData, loading: groupsLoading } = useListGroupsQuery({
    variables: { filter: { workspaceId: workspace.id }, limit: 100 },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const [updateUserGroups] = useUpdateUserGroupsMutation();

  /* ********************************* EFFECTS ******************************** */
  useEffect(() => {
    const newUsersData = usersData?.users.nodes?.[0]?.groups;
    setUserGroups(newUsersData === null ? [] : (newUsersData ?? userGroups));
  }, [usersData]);

  useEffect(() => {
    const newGroups = groupsData?.groups.nodes;
    setGroups(newGroups === null ? [] : (newGroups ?? groups));
  }, [groupsData]);

  /* ******************************* CALLBACKS ******************************** */
  const onToggleGroup = useCallback(
    async (group: RowGroup, selected: boolean) => {
      if (!user) return;
      const action = selected ? 'addedGroups' : 'removedGroups';

      await updateUserGroups({
        variables: {
          updateGroupsInput: { targetUserId: user.id, [action]: [group.id] },
        },
        refetchQueries: ['GetUsers'],
      }).catch((error) => {
        showNotification({
          title: t('Common:errors.genericTitle'),
          message: t('Common:errors.genericMessage'),
          color: 'blackroomRed',
        });
        console.error(error);
      });
    },
    [user],
  );

  /* ******************************** RENDERING ******************************* */
  return groupsLoading || usersLoading ? (
    <Center pb="xs">
      <LoaderIcon loading />
    </Center>
  ) : (
    <GroupPicker
      groups={groups}
      selectedGroups={userGroups}
      onToggleGroup={onToggleGroup}
      {...props}
    />
  );
}
